import { ReactElement } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { QuestionOption } from '../utils'

interface Props {
  /** The form field: the chosen option ids. */
  name: string
  options: QuestionOption[]
  multiple: boolean
}

const OptionLabel = ({ option }: { option: QuestionOption }): ReactElement => (
  <Stack>
    <Typography variant="body2">{option.label}</Typography>
    {option.description && (
      <Typography variant="caption" color="text.secondary">
        {option.description}
      </Typography>
    )}
  </Stack>
)

/**
 * The options of a question as a form field (the chosen ids): checkboxes for
 * several choices, radio buttons for one.
 */
const QuestionOptionsField = ({ name, options, multiple }: Props): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control })
  const selected: string[] = value ?? []

  return (
    <Stack>
      {multiple ? (
        options.map(option => (
          <FormControlLabel
            key={option.id}
            sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
            control={
              <Checkbox
                size="small"
                checked={selected.includes(option.id)}
                onChange={(_, checked) =>
                  onChange(
                    checked
                      ? options
                          .map(o => o.id)
                          .filter(id => id === option.id || selected.includes(id))
                      : selected.filter(id => id !== option.id),
                  )
                }
                data-cy={`ai-chat-question-option-${option.id}`}
              />
            }
            label={<OptionLabel option={option} />}
          />
        ))
      ) : (
        <RadioGroup value={selected[0] ?? ''} onChange={(_, id) => onChange([id])}>
          {options.map(option => (
            <FormControlLabel
              key={option.id}
              value={option.id}
              sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
              control={<Radio size="small" data-cy={`ai-chat-question-option-${option.id}`} />}
              label={<OptionLabel option={option} />}
            />
          ))}
        </RadioGroup>
      )}
      {error?.message && <FormHelperText error>{error.message}</FormHelperText>}
    </Stack>
  )
}

export default QuestionOptionsField
